import {
  MAX_CORRECTED_BEAT_RATIO,
  DETREND_METHOD,
  RESAMPLE_HZ,
  RMSSD_SOURCE,
} from './constants';
import { correctArtifacts } from './correctArtifacts';
import { computeRmssdFromNn } from './computeRmssdFromNn';
import { deriveNnFromHr } from './deriveNnFromHr';
import { resampleCubicUniform } from './resampleCubic';
import { smoothPriorsDetrend } from './smoothPriorsDetrend';
import type { HrvPreprocessResult, HrvPreprocessSegmentInput } from './types';

const round = (value: number, digits = 4) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

export const preprocessHrvPath = (
  input: HrvPreprocessSegmentInput
): HrvPreprocessResult => {
  const logs: string[] = [];
  const nn = deriveNnFromHr(input.hrValues, input.timestamps);

  if (nn.length < 10) {
    return {
      passed: false,
      correctedBeatPercent: 100,
      correctedBeatPercentPassed: false,
      totalBeats: nn.length,
      correctedBeats: 0,
      rmssd: 0,
      resampled4Hz: [],
      nnIntervals: nn,
      logs: ['Insufficient NN intervals derived from HR (< 10 beats).'],
    };
  }

  logs.push(
    `Derived ${nn.length} NN intervals from HR (proxy, Polar 360 @ 1 Hz).`
  );

  const artifact = correctArtifacts(nn);
  logs.push(
    `Artifact correction (${artifact.method}): ${artifact.correctedBeats}/${artifact.totalBeats} beats corrected (${round(artifact.correctedBeatPercent, 2)}%).`
  );

  const correctedBeatPercentPassed =
    artifact.correctedBeatPercent / 100 <= MAX_CORRECTED_BEAT_RATIO;

  if (!correctedBeatPercentPassed) {
    logs.push(
      `Session exceeds Kubios-style threshold: > ${MAX_CORRECTED_BEAT_RATIO * 100}% corrected beats.`
    );
    return {
      passed: false,
      correctedBeatPercent: round(artifact.correctedBeatPercent, 2),
      correctedBeatPercentPassed: false,
      totalBeats: artifact.totalBeats,
      correctedBeats: artifact.correctedBeats,
      rmssd: computeRmssdFromNn(artifact.intervals),
      resampled4Hz: [],
      nnIntervals: artifact.intervals,
      logs,
    };
  }

  const timestamps = artifact.intervals.map(item => item.timestamp);
  const rrValues = artifact.intervals.map(item => item.rrMs);

  const uniform1HzTimestamps: number[] = [];
  const uniform1HzValues: number[] = [];
  for (let index = 0; index < rrValues.length; index += 1) {
    uniform1HzTimestamps.push(timestamps[index]);
    uniform1HzValues.push(rrValues[index]);
  }

  const detrended = smoothPriorsDetrend(uniform1HzValues);
  logs.push(`Detrend: ${DETREND_METHOD} (lambda=500) on tachogram.`);

  const resampled4Hz = resampleCubicUniform(
    uniform1HzTimestamps,
    detrended,
    RESAMPLE_HZ
  );
  logs.push(
    `Resampled to ${RESAMPLE_HZ} Hz (cubic spline) for frequency analysis.`
  );

  const rmssd = computeRmssdFromNn(artifact.intervals);
  logs.push(`RMSSD computed from ${RMSSD_SOURCE}.`);

  return {
    passed: true,
    correctedBeatPercent: round(artifact.correctedBeatPercent, 2),
    correctedBeatPercentPassed: true,
    totalBeats: artifact.totalBeats,
    correctedBeats: artifact.correctedBeats,
    rmssd: round(rmssd, 2),
    resampled4Hz,
    nnIntervals: artifact.intervals,
    logs,
  };
};
